import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LineChart, Line,
  BarChart, Bar,
  PieChart, Pie, Cell,
  AreaChart, Area,
  ComposedChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine
} from 'recharts';
import {
  Coins, Zap, Activity, ArrowRight, Heart, Bot,
  LogOut, CreditCard, MessageSquare, Download, BarChart3,
  FileText, X, Database, ChevronDown, Star, Check,
  TrendingUp, Users, Shield, Sparkles, Lock, ImageDown
} from 'lucide-react';
import AppHeader from '../components/AppHeader';
import { InlineFeatureGate } from '../components/FeatureGate';
import { useWalkthroughActions } from '../walkthrough';


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHART_COLORS = ['#F87217', '#06B6D4', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6'];
const formatAgentName = (name = '') => String(name ?? '');


/* ── Skeleton loader ───────────────────────────────── */
function Skeleton({ className = '' }) {
  return (
    <div
      className={`rounded-xl ${className}`}
      style={{
        background: 'linear-gradient(90deg,#162040 25%,#1e2d52 50%,#162040 75%)',
        backgroundSize: '200% 100%',
        animation: 'skeleton-wave 1.4s ease-in-out infinite',
      }}
    />
  );
}

/* ── Empty chart state ─────────────────────────────── */
const EmptyChart = () => (
  <div className="h-[280px] flex items-center justify-center">
    <div className="text-center">
      <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
        <BarChart3 className="w-6 h-6 text-slate-300" />
      </div>
      <p className="text-sm font-medium text-slate-500">No data yet</p>
      <p className="text-xs text-slate-400 mt-0.5">Start chatting with agents to see your stats</p>
    </div>
  </div>
);

/* ── Stat Card ─────────────────────────────────────── */
function StatCard({ icon: Icon, iconColor, iconBg, label, value, sub, badge, progress, testId, delay = 0, isDark }) {
  const cardBg = isDark ? '#0f1729' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';

  return (
    <div
      className="rounded-xl p-3 group cursor-default"
      style={{
        background: cardBg,
        border: `1px solid ${cardBorder}`,
        boxShadow: isDark ? '0 1px 6px rgba(0,0,0,0.4)' : '0 1px 6px rgba(0,0,0,0.04)',
        transition: 'box-shadow 0.2s ease',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.boxShadow = '0 6px 18px rgba(0,0,0,0.08)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = '0 1px 6px rgba(0,0,0,0.04)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
      data-testid={testId}
    >
      {/* Top Row */}
      <div className="flex items-start justify-between mb-3">
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center"
          style={{ background: iconBg }}
        >
          <Icon className="w-4 h-4" style={{ color: iconColor }} />
        </div>

        {badge && (
          <span
            className="text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider"
            style={{ background: 'rgba(248,114,23,0.1)', color: '#F87217' }}
          >
            {badge}
          </span>
        )}
      </div>

      {/* Value */}
      <p
        className="text-lg font-bold mb-0.5"
        style={{ color: isDark ? '#f0f6fc' : '#0d1a3a', fontFamily: "'Outfit',sans-serif" }}
      >
        {value}
      </p>

      {/* Label */}
      <p className="text-xs text-slate-500 font-medium">{label}</p>

      {sub && (
        <p className="text-[11px] text-slate-400 mt-0.5">
          {sub}
        </p>
      )}

      {progress !== undefined && (
        <div className="mt-2">
          <Progress value={progress} className="h-1" />
        </div>
      )}
    </div>
  );
}

/* ── Chart Card ────────────────────────────────────── */
function ChartCard({ title, subtitle, children, className = '', isDark, onDownload, downloading }) {
  const cardBg = isDark ? '#0f1729' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)';
  return (
    <div
      data-chart-capture
      className={`rounded-2xl p-6 flex flex-col ${className}`}
      style={{ background: cardBg, border: `1px solid ${cardBorder}`, boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.4)' : '0 2px 8px rgba(0,0,0,0.04)' }}
    >
      <div className="flex items-start justify-between gap-3 shrink-0">
        <div>
          <h3 className="text-base font-bold mb-0.5" style={{ fontFamily: "'Outfit',sans-serif", color: isDark ? '#f0f6fc' : '#0d1a3a' }}>{title}</h3>
          {subtitle && <p className="text-xs mb-5" style={{ color: isDark ? '#6e7681' : '#94a3b8' }}>{subtitle}</p>}
        </div>
        {onDownload && (
          <button
            onClick={onDownload}
            disabled={downloading}
            data-download-btn
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-all duration-200"
            style={{
              background: isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'}`,
              color: isDark ? '#94a3b8' : '#475569',
              opacity: downloading ? 0.6 : 1,
              cursor: downloading ? 'not-allowed' : 'pointer',
            }}
          >
            <ImageDown className="w-3.5 h-3.5" />
            {downloading ? 'Downloading…' : 'Download'}
          </button>
        )}
      </div>
      <div className="flex-1 flex flex-col justify-end min-h-[0]">
        {children}
      </div>
    </div>
  );
}

/* ── Plan Card (consistent with Landing/Pricing) ───── */
function PlanCard({ plan, isCurrent, onSelect, index, isDark }) {
  const isPopular = plan.name === 'Pro';
  const cardBg = isPopular ? 'linear-gradient(145deg,#F87217,#FF9A4E)' : (isDark ? '#0f1729' : '#fff');
  const cardBorder = isPopular ? 'none' : `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)'}`;
  return (
    <div
      className="relative rounded-2xl flex flex-col transition-all duration-300"
      style={{
        background: cardBg,
        border: cardBorder,
        boxShadow: isPopular
          ? '0 20px 56px rgba(248,114,23,0.32), 0 8px 24px rgba(248,114,23,0.16)'
          : '0 2px 8px rgba(0,0,0,0.04)',
        padding: isPopular ? '32px 28px' : '28px',
        animation: `fadeUp 0.5s ease ${index * 0.08}s both`,
      }}
      data-testid={`plan-card-${plan.name.toLowerCase()}`}
    >
      {isPopular && (
        <div
          className="absolute -top-3.5 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-4 py-1 text-xs font-bold rounded-full whitespace-nowrap"
          style={{ background: '#0d1a3a', color: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.25)' }}
        >
          <Star className="w-3 h-3 fill-[#F87217] stroke-none" />
          MOST POPULAR
        </div>
      )}

      <h3
        className="text-lg font-bold mb-1"
        style={{ fontFamily: "'Outfit',sans-serif", color: isPopular ? '#fff' : '#0d1a3a' }}
      >
        {plan.name}
      </h3>
      <div className="flex items-end gap-1 mt-3 mb-2">
        <span className="text-4xl font-extrabold tracking-tight" style={{ color: isPopular ? '#fff' : '#0d1a3a', fontFamily: "'Outfit',sans-serif" }}>
          {plan.price}
        </span>
        <span className="mb-1.5 text-sm" style={{ color: isPopular ? 'rgba(255,255,255,0.65)' : '#94a3b8' }}>/mo</span>
      </div>

      <div
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold mb-5"
        style={{
          background: isPopular ? 'rgba(255,255,255,0.18)' : 'rgba(248,114,23,0.08)',
          color: isPopular ? '#fff' : '#F87217',
        }}
      >
        <Zap className="w-3 h-3" />
        {plan.credits?.toLocaleString?.() ?? plan.credits} credits
      </div>

      <ul className="space-y-2.5 mb-6 flex-1">
        {plan.features.map((f, j) => (
          <li key={j} className="flex items-center gap-2 text-xs"
            style={{ color: isPopular ? 'rgba(255,255,255,0.88)' : '#475569' }}>
            <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: isPopular ? 'rgba(255,255,255,0.22)' : 'rgba(248,114,23,0.1)' }}>
              <Check className="w-2.5 h-2.5" style={{ color: isPopular ? '#fff' : '#F87217' }} />
            </div>
            {f}
          </li>
        ))}
      </ul>

      {isCurrent ? (
        <div
          className="w-full py-2.5 text-center rounded-xl text-xs font-bold"
          style={{
            background: isPopular ? 'rgba(255,255,255,0.15)' : '#f1f5f9',
            color: isPopular ? 'rgba(255,255,255,0.7)' : '#64748b',
          }}
        >
          ✓ Current Plan
        </div>
      ) : (
        <button
          onClick={() => onSelect(plan)}
          className="w-full py-2.5 rounded-xl text-xs font-bold transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
          style={{
            background: isPopular ? 'rgba(255,255,255,0.95)' : 'linear-gradient(135deg,#F87217,#FF9A4E)',
            color: isPopular ? '#F87217' : '#fff',
            boxShadow: isPopular ? '0 4px 12px rgba(0,0,0,0.1)' : '0 6px 18px rgba(248,114,23,0.32)',
          }}
          onMouseEnter={e => { if (!isPopular) e.currentTarget.style.boxShadow = '0 10px 24px rgba(248,114,23,0.45)'; }}
          onMouseLeave={e => { if (!isPopular) e.currentTarget.style.boxShadow = '0 6px 18px rgba(248,114,23,0.32)'; }}
          data-testid={`upgrade-to-${plan.name.toLowerCase()}`}
        >
          {plan.name === 'Free' ? 'Get Started Free' : `Upgrade to ${plan.name}`}
        </button>
      )}
    </div>
  );
}

/* ── Agent mini-card ───────────────────────────────── */
function AgentMiniCard({ agent, onClick, testId, isDark }) {
  const cardBg = isDark ? '#0f1729' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)';
  return (
    <div
      className="group p-4 rounded-2xl cursor-pointer transition-all duration-200 relative"
      style={{ background: cardBg, border: `1px solid ${cardBorder}`, boxShadow: isDark ? '0 1px 4px rgba(0,0,0,0.4)' : '0 1px 4px rgba(0,0,0,0.04)' }}
      onClick={onClick}
      data-testid={testId}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = 'rgba(248,114,23,0.35)';
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(248,114,23,0.12)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)';
        e.currentTarget.style.boxShadow = isDark ? '0 1px 4px rgba(0,0,0,0.4)' : '0 1px 4px rgba(0,0,0,0.04)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <p className="text-[10px] font-bold uppercase tracking-widest text-[#F87217] mb-1">{agent.category}</p>
      <h3 className="font-semibold text-sm mb-1 group-hover:text-[#F87217] transition-colors" style={{ color: isDark ? '#e6edf3' : '#0d1a3a' }}>{formatAgentName(agent.name)}</h3>
      <p className="text-xs" style={{ color: isDark ? '#6e7681' : '#94a3b8' }}>{agent.credits_per_query} credits/query</p>
    </div>
  );
}

/* ── Dashboard ─────────────────────────────────────── */
const Dashboard = () => {
  const { user, token, logout, isAuthenticated } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const chartsRef = useRef(null);
  const usageChartRef = useRef(null);
  const creditsByAgentChartRef = useRef(null);
  const usageByCategoryChartRef = useRef(null);
  const creditBurnChartRef = useRef(null);
  const promptsPerDayChartRef = useRef(null);
  const [downloadingChartKey, setDownloadingChartKey] = useState('');

  const [stats, setStats] = useState(null);
  const [agents, setAgents] = useState([]);
  const [plans, setPlans] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [myAgents, setMyAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [agentDropdownOpen, setAgentDropdownOpen] = useState(false);
  const [ibmDropdownOpen, setIbmDropdownOpen] = useState(false);
  const [openaiDropdownOpen, setOpenaiDropdownOpen] = useState(false);

  // LLM Preference state
  const [llmPreference, setLlmPreference] = useState({ provider: 'default', model_id: '' });
  const [availableModels, setAvailableModels] = useState(null);
  const [savingLlm, setSavingLlm] = useState(false);

  const [visible, setVisible] = useState(false);
  const { isAdmin } = useAuth();
  const { isSuperAdmin } = useAuth();
  const { resetTour } = useWalkthroughActions();


  useEffect(() => {
    if (loading) return;  // wait for auth to resolve
    if (!isAuthenticated) { navigate('/login'); return; }
    fetchData();
  }, [isAuthenticated, loading, navigate]);


  /* ── Download one chart as a PNG ─────── */
  const downloadChart = async (chartRef, chartKey, filePrefix) => {
    // The ref points to the wrapper div; the ChartCard itself has data-chart-capture
    const container = chartRef?.current;
    if (!container) return;
    // Target the ChartCard div (has data-chart-capture) or fall back to container
    const captureTarget = container.querySelector('[data-chart-capture]') || container;
    if (!captureTarget.querySelector('svg')) { toast.error('No chart to download'); return; }

    setDownloadingChartKey(chartKey);
    try {
      const PADDING = 24; // extra whitespace around chart in exported image

      // Temporarily expand any scrollable children so we measure their FULL height,
      // not just the clipped viewport height. This gives html2canvas enough canvas
      // space so the onclone expansion actually shows all content.
      const scrollableEls = captureTarget.querySelectorAll('.overflow-y-auto, .overflow-auto');
      const savedScrollStyles = [];
      scrollableEls.forEach((el) => {
        savedScrollStyles.push({
          el,
          maxHeight: el.style.maxHeight,
          height: el.style.height,
          overflow: el.style.overflow,
        });
        el.style.maxHeight = 'none';
        el.style.height = 'auto';
        el.style.overflow = 'visible';
      });

      // Now measure the full rendered size (scrollable areas are expanded)
      const rect = captureTarget.getBoundingClientRect();
      const naturalW = Math.ceil(Math.max(captureTarget.scrollWidth, rect.width, 400));
      const naturalH = Math.ceil(Math.max(captureTarget.scrollHeight, rect.height, 200));

      // Restore original scrollable styles immediately (only the canvas clone will stay expanded)
      savedScrollStyles.forEach(({ el, maxHeight, height, overflow }) => {
        el.style.maxHeight = maxHeight;
        el.style.height = height;
        el.style.overflow = overflow;
      });

      const canvas = await html2canvas(captureTarget, {
        backgroundColor: isDark ? '#0f1729' : '#ffffff',
        scale: 2,                         // fixed 2× for crisp output
        width: naturalW,
        height: naturalH,
        windowWidth: naturalW + PADDING * 2,
        windowHeight: naturalH + PADDING * 2,
        x: 0,
        y: 0,
        scrollX: 0,
        scrollY: 0,
        useCORS: true,
        allowTaint: true,
        logging: false,
        onclone: (_clonedDoc, clonedEl) => {
          // Hide download button so it doesn't appear in exported image
          clonedEl.querySelectorAll('[data-download-btn]').forEach((btn) => {
            btn.style.display = 'none';
          });

          // Force every ancestor/descendant overflow to visible so nothing clips
          const expand = (el) => {
            el.style.overflow = 'visible';
            el.style.maxHeight = 'none';
            el.style.maxWidth = 'none';
            el.style.height = 'auto';
            el.style.width = naturalW + 'px';
          };
          expand(clonedEl);

          // Expand all inner scroll/clipping containers
          clonedEl.querySelectorAll('*').forEach((node) => {
            const cs = window.getComputedStyle(node);
            const overflowHidden =
              cs.overflow === 'hidden' ||
              cs.overflowX === 'hidden' ||
              cs.overflowY === 'hidden';
            if (overflowHidden) {
              node.style.overflow = 'visible';
              node.style.maxHeight = 'none';
            }
            // Expand scrollable legend areas
            if (
              node.classList.contains('overflow-y-auto') ||
              node.classList.contains('overflow-auto') ||
              node.style.overflowY === 'auto'
            ) {
              node.style.overflow = 'visible';
              node.style.maxHeight = 'none';
              node.style.height = 'auto';
            }
          });

          // Make recharts ResponsiveContainer fill the full natural width
          clonedEl.querySelectorAll('.recharts-responsive-container').forEach((rc) => {
            rc.style.width = naturalW + 'px';
            rc.style.height = 'auto';
            rc.style.minHeight = '150px';
          });

          // Ensure SVGs are not clipped
          clonedEl.querySelectorAll('svg').forEach((svg) => {
            svg.style.overflow = 'visible';
            // Remove any viewBox-based clipping constraints
            svg.removeAttribute('clip-path');
          });
        },
      });

      // Add padding around the chart in the final image
      const paddedCanvas = document.createElement('canvas');
      paddedCanvas.width = canvas.width + PADDING * 2 * 2;   // *2 because scale=2
      paddedCanvas.height = canvas.height + PADDING * 2 * 2;
      const ctx = paddedCanvas.getContext('2d');
      ctx.fillStyle = isDark ? '#0a0f1e' : '#f8fafc';
      ctx.fillRect(0, 0, paddedCanvas.width, paddedCanvas.height);
      ctx.drawImage(canvas, PADDING * 2, PADDING * 2);

      paddedCanvas.toBlob((blob) => {
        if (!blob) { toast.error('Failed to generate chart image'); return; }
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${filePrefix}-${new Date().toISOString().slice(0, 10)}.png`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Chart downloaded!');
      }, 'image/png');
    } catch (err) {
      console.error('Chart download error:', err);
      toast.error('Failed to download chart');
    } finally {
      setDownloadingChartKey('');
    }
  };

  const fetchData = async () => {
    const currentToken = localStorage.getItem('token')
    if (!currentToken || currentToken === 'null' || currentToken === 'undefined' || currentToken.split('.').length !== 3) {
      toast.error('Session expired. Please login again.');
      navigate('/login');
      return;
    }
    try {
      const [statsRes, agentsRes, plansRes, favoritesRes, myAgentsRes, prefRes, modelsRes] = await Promise.all([
        axios.get(`${API}/usage/stats`, { headers: { Authorization: `Bearer ${currentToken}` } }),
        axios.get(`${API}/agents`),
        axios.get(`${API}/subscriptions/plans`),
        axios.get(`${API}/favorites`, { headers: { Authorization: `Bearer ${currentToken}` } }),
        axios.get(`${API}/custom-agents/user/me`, { headers: { Authorization: `Bearer ${currentToken}` } }),
        axios.get(`${API}/auth/llm-preference`, { headers: { Authorization: `Bearer ${currentToken}` } }).catch(() => ({ data: { provider: 'default', model_id: '' } })),
        axios.get(`${API}/auth/llm-models`, { headers: { Authorization: `Bearer ${currentToken}` } }).catch(() => ({ data: null }))
      ]);
      setStats(statsRes.data);
      setAgents(Array.isArray(agentsRes.data) ? agentsRes.data : []);
      setPlans(Array.isArray(plansRes.data) ? plansRes.data : []);
      setLlmPreference(prefRes.data);
      setAvailableModels(modelsRes.data);
      const favData = favoritesRes.data;
      setFavorites(Array.isArray(favData) ? favData : Array.isArray(favData?.favorites) ? favData.favorites : []);
      setMyAgents(Array.isArray(myAgentsRes.data) ? myAgentsRes.data : []);
      try {
        const analyticsRes = await axios.get(`${API}/usage/analytics`, { headers: { Authorization: `Bearer ${token}` } });
        setAnalytics(analyticsRes.data);
      } catch { /* analytics unavailable */ }
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  /* Route user to /payment with plan data so payment portal knows what's being purchased */
  const handleSelectPlan = (plan) => {
    const planLower = plan.name.toLowerCase();
    if (planLower === 'free') { navigate('/dashboard'); return; }

    navigate('/payment', {
      state: {
        orderId: `plan-${planLower}-${Date.now()}`,
        orderItems: [
          {
            id: `plan-${planLower}`,
            name: `${plan.name} Plan — Monthly`,
            quantity: 1,
            price: parseFloat(plan.price.replace('$', '')) || 0,
          },
        ],
        planName: plan.name,
        planCredits: plan.credits,
      },
    });
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const exportAllChats = async (format) => {
    try {
      const response = await axios.get(`${API}/chat/export-all?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { filename, content } = response.data;
      const blob = format === 'csv'
        ? new Blob([content], { type: 'text/csv' })
        : new Blob([JSON.stringify(content, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch { toast.error('Failed to export chat history'); }
  };

  const getPlanCredits = () => {
    switch (user?.plan) {
      case 'free': return 100;
      case 'pro': return 5000;
      case 'enterprise': return 50000;
      case 'enterprise_pro': return 80000;
      default: return 100;
    }
  };
  const maxCredits = getPlanCredits();
  const actualCreditsUsed = stats?.credits_used || 0;
  const currentCredits = Math.max(maxCredits - actualCreditsUsed, 0);
  const creditsUsed = actualCreditsUsed;
  const creditsUsedPct = Math.min((creditsUsed / maxCredits) * 100, 100);

  /* Loading skeleton removed — dashboard shows immediately */

  const dashboardRightContent = (
    <>
      <div
        className="flex items-center gap-2 px-3 py-2 rounded-xl"
        style={{ background: 'rgba(248,114,23,0.08)', border: '1px solid rgba(248,114,23,0.15)' }}
      >
        <Coins className="w-4 h-4 text-[#F87217]" />
        <span className="text-sm font-bold" style={{ color: isDark ? '#f0f6fc' : '#0d1a3a' }}>{currentCredits}</span>
        <span className="text-xs text-slate-500">credits</span>
      </div>
      <button
        onClick={handleLogout}
        className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200"
        style={{ color: isDark ? '#94a3b8' : '#475569' }}
        onMouseEnter={e => { e.currentTarget.style.color = isDark ? '#f0f6fc' : '#0f172a'; e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)'; }}
        onMouseLeave={e => { e.currentTarget.style.color = isDark ? '#94a3b8' : '#475569'; e.currentTarget.style.background = 'transparent'; }}
        data-testid="logout-btn"
      >
        <LogOut className="w-4 h-4" />
        Logout
      </button>
    </>
  );

  return (
    <div className="min-h-screen" style={{ background: isDark ? '#0a0f1e' : '#f8fafc' }}>
      <AppHeader rightContent={dashboardRightContent} />

      <div
        className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-6 md:py-10"
      >
        {/* ── Welcome Header ─────────────────────────────── */}
        <div className="mb-10" data-tour="dashboard-header">

          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-extrabold" style={{ fontFamily: "'Outfit',sans-serif", letterSpacing: '-0.02em', color: isDark ? '#f0f6fc' : '#0d1a3a' }}>
                Welcome back, {user?.name?.split(' ')[0]}
              </h1>
              <p className="text-slate-500 mt-1 text-sm">Here's your AI agent usage overview</p>
            </div>
            {/* Current plan badge & Admin Button */}
            <div className="flex items-center gap-3">
              {isAdmin && (
                <button
                  onClick={() => navigate('/admin')}
                  className="flex items-center justify-center gap-2 px-4 rounded-xl text-sm font-bold transition-all duration-200 h-[40px]"
                  style={{
                    background: '#F87217',
                    color: '#fff',
                    boxShadow: '0 4px 12px rgba(248,114,23,0.25)',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(248,114,23,0.35)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(248,114,23,0.25)'; }}
                >
                  <Shield className="w-4 h-4" />
                  {isSuperAdmin ? 'SuperAdmin Dashboard' : 'Admin Dashboard'}
                </button>
              )}
              <div
                className="flex items-center gap-2 px-4 rounded-xl text-sm h-[40px]"
                style={{
                  background: 'rgba(248,114,23,0.08)',
                  border: '1px solid rgba(248,114,23,0.18)',
                }}
              >
                <Sparkles className="w-4 h-4 text-[#F87217]" />
                <span className="font-bold capitalize" style={{ color: isDark ? '#f0f6fc' : '#0d1a3a' }}>{user?.plan?.replace('_', ' ')}</span>
                <span className="text-slate-500">plan</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Stats Grid (Compact) ───────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-tour="stats-grid">

          <StatCard
            icon={Coins}
            iconColor="#F87217"
            iconBg="rgba(248,114,23,0.08)"
            label="Credits Remaining"
            value={currentCredits}
            sub={`of ${maxCredits} total`}
            badge={user?.plan?.toUpperCase()}
            progress={100 - creditsUsedPct}
            testId="stat-credits"
            delay={0}
            compact
            isDark={isDark}
          />

          <StatCard
            icon={MessageSquare}
            iconColor="#3B82F6"
            iconBg="rgba(59,130,246,0.08)"
            label="Total Queries"
            value={stats?.total_queries || 0}
            sub="All time"
            testId="stat-queries"
            delay={80}
            compact
            isDark={isDark}
          />

          <StatCard
            icon={Zap}
            iconColor="#F59E0B"
            iconBg="rgba(245,158,11,0.08)"
            label="Credits Used"
            value={stats?.credits_used || 0}
            sub="This billing cycle"
            testId="stat-credits-used"
            delay={160}
            compact
            isDark={isDark}
          />

          <StatCard
            icon={Activity}
            iconColor="#10B981"
            iconBg="rgba(16,185,129,0.08)"
            label="Agents Used"
            value={stats?.agents_used?.length || 0}
            sub="Unique agents"
            testId="stat-agents-used"
            delay={240}
            compact
            isDark={isDark}
          />
        </div>

        {/* ── Main Charts + Quick Actions ────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-4 md:gap-6">
          {/* Usage Over Time (main chart) */}
          {/* Usage Over Time (Smaller Version) */}
          <div ref={usageChartRef} className="col-span-1 lg:col-span-2 h-full">
            <ChartCard
              title="Usage Over Time"
              subtitle="Daily credit consumption"
              className="!p-4 h-full"
              isDark={isDark}
              onDownload={() => downloadChart(usageChartRef, 'usage-over-time', 'usage-over-time')}
              downloading={downloadingChartKey === 'usage-over-time'}
            >
              {stats?.daily_usage?.length > 0 ? (
                <ResponsiveContainer width="100%" height={340}>
                  <LineChart
                    data={stats.daily_usage}
                    margin={{ top: 10, right: 20, left: 10, bottom: 50 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.06)' : '#f0f4f8'} vertical={false} />
                    <XAxis
                      dataKey="date"
                      stroke="#94a3b8"
                      fontSize={8}
                      interval={0}
                      angle={-35}
                      textAnchor="end"
                      tick={{ fill: '#94a3b8' }}
                      tickMargin={2}
                      tickFormatter={(v) => String(v).slice(5)}
                      label={{ value: 'Date', position: 'insideBottom', offset: -10, fontSize: 10, fill: '#94a3b8' }}
                    />
                    <YAxis
                      stroke="#94a3b8"
                      fontSize={9}
                      width={50}
                      tick={{ fill: '#94a3b8' }}
                      allowDecimals={false}
                      label={{ value: 'Credits', angle: -90, position: 'insideLeft', offset: 10, fontSize: 10, fill: '#94a3b8' }}
                    />
                    <Tooltip
                      contentStyle={{
                        background: '#fff',
                        border: '1px solid rgba(0,0,0,0.08)',
                        borderRadius: 8,
                        fontSize: 11
                      }}
                      formatter={(v) => [`${v} credits`, 'Usage']}
                      labelFormatter={(label) => `Date: ${label}`}
                    />
                    <Line
                      type="monotone"
                      dataKey="credits"
                      stroke="#F87217"
                      strokeWidth={2}
                      dot={{ fill: '#F87217', r: 4, strokeWidth: 0 }}
                      activeDot={{ r: 6, fill: '#FF4F00' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChart />
              )}
            </ChartCard>
          </div>


          {/* Quick Actions */}
          <div
            data-tour="quick-actions"
            className="col-span-1 lg:col-span-4 rounded-2xl p-4 md:p-6 flex flex-col gap-4"
            style={{ background: isDark ? '#0f1729' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)'}`, boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.4)' : '0 2px 8px rgba(0,0,0,0.04)' }}
          >
            <h2 className="text-base font-bold" style={{ fontFamily: "'Outfit',sans-serif", color: isDark ? '#f0f6fc' : '#0d1a3a' }}>Quick Actions</h2>


            <button
              onClick={() => navigate('/marketplace')}
              className="group w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-bold text-white transition-all duration-200"
              style={{ background: 'linear-gradient(135deg,#F87217,#FF9A4E)', boxShadow: '0 4px 16px rgba(248,114,23,0.3)' }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 24px rgba(248,114,23,0.45)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(248,114,23,0.3)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              data-testid="explore-agents-action"
            >
              Explore Agents
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>

            <button
              onClick={() => navigate('/pricing')}
              data-tour="billing-plans"
              className="group w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-200"
              style={{ background: isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`, color: isDark ? '#c9d1d9' : '#374151' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(248,114,23,0.35)'; e.currentTarget.style.background = isDark ? 'rgba(248,114,23,0.08)' : '#fff'; e.currentTarget.style.color = isDark ? '#f0f6fc' : '#0d1a3a'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'; e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc'; e.currentTarget.style.color = isDark ? '#c9d1d9' : '#374151'; }}
              data-testid="upgrade-plan-action"
            >
              View Pricing
              <CreditCard className="w-4 h-4" style={{ color: isDark ? '#6e7681' : '#94a3b8' }} />
            </button>

            {/* Restart Product Tour */}
            <button
              onClick={resetTour}
              data-tour="restart-tour-btn"
              className="group w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-200"
              style={{ background: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc', border: `1px solid ${isDark ? 'rgba(248,114,23,0.15)' : 'rgba(248,114,23,0.2)'}`, color: isDark ? '#F87217' : '#C65B12' }}
              onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(248,114,23,0.08)' : '#fff7ed'; e.currentTarget.style.borderColor = 'rgba(248,114,23,0.35)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc'; e.currentTarget.style.borderColor = isDark ? 'rgba(248,114,23,0.15)' : 'rgba(248,114,23,0.2)'; }}
              aria-label="Restart the product tour"
            >
              Restart Product Tour
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>

            {/* Export */}
            {/* <div className="pt-1" style={{ borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9'}` }}>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Export History</p>
              <div className="flex gap-2">
                <button
                  onClick={() => exportAllChats('json')}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold transition-all duration-200"
                  style={{ background: isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`, color: isDark ? '#c9d1d9' : '#475569' }}
                  onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(248,114,23,0.1)' : '#fff'; e.currentTarget.style.borderColor = 'rgba(248,114,23,0.3)'; e.currentTarget.style.color = isDark ? '#F87217' : '#475569'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc'; e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'; e.currentTarget.style.color = isDark ? '#c9d1d9' : '#475569'; }}
                  data-testid="export-all-json"
                >
                  <Download className="w-3 h-3" /> JSON
                </button>
                <button
                  onClick={() => exportAllChats('csv')}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold transition-all duration-200"
                  style={{ background: isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`, color: isDark ? '#c9d1d9' : '#475569' }}
                  onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(248,114,23,0.1)' : '#fff'; e.currentTarget.style.borderColor = 'rgba(248,114,23,0.3)'; e.currentTarget.style.color = isDark ? '#F87217' : '#475569'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc'; e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'; e.currentTarget.style.color = isDark ? '#c9d1d9' : '#475569'; }}
                  data-testid="export-all-csv"
                >
                  <Download className="w-3 h-3" /> CSV
                </button>
              </div>
            </div> */}

            {/* Knowledge Base */}
            <div className="pt-1" style={{ borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9'}` }}>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Knowledge Base</p>
              <InlineFeatureGate feature="knowledge_base" featureLabel="Knowledge Base">
                <p className="text-xs text-slate-400 leading-relaxed mb-3">
                  Upload documents for HR, Legal, Finance and connect them to any agent.
                </p>
                <button
                  onClick={() => navigate('/knowledge-base')}
                  className="group w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-all duration-200"
                  style={{ background: '#0d1a3a' }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#1e293b'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#0d1a3a'; }}
                  data-testid="manage-kb-action"
                >
                  <span className="flex items-center gap-2"><Database className="w-4 h-4" /> Manage KB</span>
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>
              </InlineFeatureGate>
            </div>

            {/* Agent selector */}
            <div className="pt-1" data-tour="settings-llm" style={{ borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9'}` }}>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Select Agent</p>

              <InlineFeatureGate feature="select_agent" featureLabel="Agent Selection">
                <div className="relative">
                  <button
                    onClick={() => { setAgentDropdownOpen(v => !v); setIbmDropdownOpen(false); setOpenaiDropdownOpen(false); }}
                    className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-all duration-200"
                    style={{ background: 'linear-gradient(135deg,#F87217,#FF9A4E)' }}
                    data-testid="select-agent-dropdown"
                    disabled={savingLlm}
                  >
                    <span className="truncate">
                      {savingLlm ? 'Saving...' :
                        llmPreference.provider === 'watsonx' && llmPreference.model_id
                          ? `IBM: ${availableModels?.watsonx?.models?.find(m => m.id === llmPreference.model_id)?.name || llmPreference.model_id}`
                          : llmPreference.provider === 'openai' && llmPreference.model_id
                            ? `Azure OpenAI: ${availableModels?.openai?.models?.find(m => m.id === llmPreference.model_id)?.name || llmPreference.model_id}`
                            : 'Auto'}
                    </span>
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${agentDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {agentDropdownOpen && (
                    <div
                      className="absolute mt-2 w-full rounded-xl overflow-hidden z-50 flex flex-col"
                      style={{ background: isDark ? '#0f1729' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`, boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}
                    >
                      {/* Default Option */}
                      <button
                        onClick={async () => {
                          setSavingLlm(true);
                          try {
                            await axios.put(`${API}/auth/llm-preference`, { provider: 'default' }, { headers: { Authorization: `Bearer ${token}` } });
                            setLlmPreference({ provider: 'default', model_id: '' });
                            toast.success("Switched to llama-3.3-70b-versatile");
                          } catch (err) {
                            toast.error("Failed to save preference");
                          } finally {
                            setSavingLlm(false);
                            setAgentDropdownOpen(false);
                          }
                        }}
                        className="w-full text-left px-4 py-2.5 text-sm flex items-center justify-between transition-colors"
                        style={{ background: isDark ? '#0f1729' : '#fff', color: isDark ? '#c9d1d9' : '#374151' }}
                        onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(248,114,23,0.1)' : '#fff7ed'; e.currentTarget.style.color = '#F87217'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = isDark ? '#0f1729' : '#fff'; e.currentTarget.style.color = isDark ? '#c9d1d9' : '#374151'; }}
                      >
                        <span>Auto</span>
                        {llmPreference.provider === 'default' && <Check className="w-4 h-4 text-[#F87217]" />}
                      </button>

                      {/* IBM Watsonx Nested Option */}
                      <div className="relative" style={{ borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9'}` }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); setIbmDropdownOpen(v => !v); }}
                          className="w-full text-left px-4 py-2.5 text-sm flex items-center justify-between transition-colors"
                          style={{ background: isDark ? '#0f1729' : '#fff', color: isDark ? '#c9d1d9' : '#374151' }}
                          onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(248,114,23,0.1)' : '#fff7ed'; e.currentTarget.style.color = '#F87217'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = isDark ? '#0f1729' : '#fff'; e.currentTarget.style.color = isDark ? '#c9d1d9' : '#374151'; }}
                        >
                          <span className="flex items-center gap-2">
                            IBM Watsonx
                            {!availableModels?.watsonx?.available && <span className="text-[9px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-bold">Unconfigured</span>}
                          </span>
                          <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${ibmDropdownOpen ? 'rotate-180' : '-rotate-90'}`} />
                        </button>

                        {ibmDropdownOpen && availableModels?.watsonx?.available && (
                          <div className="py-1" style={{ background: isDark ? 'rgba(0,0,0,0.2)' : '#f8fafc', borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9'}`, borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9'}` }}>
                            {availableModels.watsonx.models.map(model => (
                              <button
                                key={model.id}
                                onClick={async () => {
                                  setSavingLlm(true);
                                  try {
                                    await axios.put(`${API}/auth/llm-preference`, { provider: 'watsonx', model_id: model.id }, { headers: { Authorization: `Bearer ${token}` } });
                                    setLlmPreference({ provider: 'watsonx', model_id: model.id });
                                    toast.success(`Switched to IBM: ${model.name}`);
                                  } catch (err) {
                                    toast.error("Failed to save IBM preference");
                                  } finally {
                                    setSavingLlm(false);
                                    setAgentDropdownOpen(false);
                                    setIbmDropdownOpen(false);
                                  }
                                }}
                                className="w-full text-left px-8 py-2 text-xs flex items-center justify-between transition-colors"
                                style={{ color: isDark ? '#94a3b8' : '#475569' }}
                                onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'; e.currentTarget.style.color = isDark ? '#f0f6fc' : '#0f172a'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = isDark ? '#94a3b8' : '#475569'; }}
                              >
                                <div className="flex flex-col">
                                  <span className={llmPreference.model_id === model.id ? 'font-bold text-[#F87217]' : 'font-medium'}>{model.name}</span>
                                  <span className="text-[10px] text-slate-400 opacity-80">{model.description}</span>
                                </div>
                                {llmPreference.provider === 'watsonx' && llmPreference.model_id === model.id && (
                                  <Check className="w-3.5 h-3.5 text-[#F87217]" />
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* OpenAI Nested Option */}
                      <div className="relative" style={{ borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#f1f5f9'}` }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); setOpenaiDropdownOpen(v => !v); }}
                          className="w-full text-left px-4 py-2.5 text-sm flex items-center justify-between transition-colors"
                          style={{ background: isDark ? '#0f1729' : '#fff', color: isDark ? '#c9d1d9' : '#374151' }}
                          onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(248,114,23,0.1)' : '#fff7ed'; e.currentTarget.style.color = '#F87217'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = isDark ? '#0f1729' : '#fff'; e.currentTarget.style.color = isDark ? '#c9d1d9' : '#374151'; }}
                        >
                          <span className="flex items-center gap-2">
                            Azure OpenAI
                            {!availableModels?.openai?.available && <span className="text-[9px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-bold">Unconfigured</span>}
                          </span>
                          <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${openaiDropdownOpen ? 'rotate-180' : '-rotate-90'}`} />
                        </button>

                        {openaiDropdownOpen && availableModels?.openai?.available && (
                          <div className="py-1" style={{ background: isDark ? 'rgba(0,0,0,0.2)' : '#f8fafc', borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9'}`, borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9'}` }}>
                            {availableModels.openai.models.map(model => (
                              <button
                                key={model.id}
                                onClick={async () => {
                                  setSavingLlm(true);
                                  try {
                                    await axios.put(`${API}/auth/llm-preference`, { provider: 'openai', model_id: model.id }, { headers: { Authorization: `Bearer ${token}` } });
                                    setLlmPreference({ provider: 'openai', model_id: model.id });
                                    toast.success(`Switched to Azure OpenAI: ${model.name}`);
                                  } catch (err) {
                                    toast.error("Failed to save Azure OpenAI preference");
                                  } finally {
                                    setSavingLlm(false);
                                    setAgentDropdownOpen(false);
                                    setOpenaiDropdownOpen(false);
                                  }
                                }}
                                className="w-full text-left px-8 py-2 text-xs flex items-center justify-between transition-colors"
                                style={{ color: isDark ? '#94a3b8' : '#475569' }}
                                onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'; e.currentTarget.style.color = isDark ? '#f0f6fc' : '#0f172a'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = isDark ? '#94a3b8' : '#475569'; }}
                              >
                                <div className="flex flex-col">
                                  <span className={llmPreference.model_id === model.id ? 'font-bold text-[#F87217]' : 'font-medium'}>{model.name}</span>
                                  <span className="text-[10px] text-slate-400 opacity-80">{model.description}</span>
                                </div>
                                {llmPreference.provider === 'openai' && llmPreference.model_id === model.id && (
                                  <Check className="w-3.5 h-3.5 text-[#F87217]" />
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </InlineFeatureGate>
            </div>
          </div>
        </div>

        {/* ── Analytics Charts (Horizontal Layout) ────────────────────────────── */}
        <div className="mb-10">
          <div className="flex flex-wrap items-center gap-2">
            <h2
              className="text-xl font-bold mb-0"
              style={{ fontFamily: "'Outfit',sans-serif", color: isDark ? '#f0f6fc' : '#0d1a3a' }}
            >
              Usage Analytics
            </h2>
          </div>

          <div ref={chartsRef} className="grid grid-cols-1 sm:grid-cols-2 gap-4">

            {/* 1️⃣ Credits per Agent */}
            <div ref={creditsByAgentChartRef} className="h-full">
              <ChartCard
                title="Credits per Agent"
                subtitle="Budget allocation"
                className="!p-4 h-full"
                isDark={isDark}
                onDownload={() => downloadChart(creditsByAgentChartRef, 'credits-per-agent', 'credits-per-agent')}
                downloading={downloadingChartKey === 'credits-per-agent'}
              >
                {analytics?.credits_per_agent?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart
                      layout="vertical"
                      data={analytics.credits_per_agent}
                      margin={{ left: 0, right: 20, top: 5, bottom: 25 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.06)' : '#f0f4f8'} horizontal={false} />
                      <XAxis type="number" stroke="#94a3b8" fontSize={9} label={{ value: 'Credits Used', position: 'insideBottom', offset: -15, fontSize: 9, fill: '#94a3b8' }} />
                      <YAxis type="category" dataKey="agent_name" width={70} stroke="#94a3b8" fontSize={8} label={{ value: 'Agent', angle: -90, position: 'insideLeft', offset: 15, fontSize: 9, fill: '#94a3b8' }} />
                      <Tooltip />
                      <Bar dataKey="credits_used" fill="#F87217" radius={[0, 3, 3, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <EmptyChart />}
              </ChartCard>
            </div>

            {/* 2️⃣ Usage by Category */}
            <div ref={usageByCategoryChartRef} className="h-full">
              <ChartCard
                title="Usage by Category"
                subtitle="Diversity of agent use"
                className="!p-4 h-full"
                isDark={isDark}
                onDownload={() => downloadChart(usageByCategoryChartRef, 'usage-by-category', 'usage-by-category')}
                downloading={downloadingChartKey === 'usage-by-category'}
              >
                {analytics?.usage_by_category?.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={150}>
                      <PieChart>
                        <Pie
                          data={analytics.usage_by_category}
                          cx="50%"
                          cy="50%"
                          innerRadius={38}
                          outerRadius={62}
                          dataKey="credits_used"
                          nameKey="category"
                          paddingAngle={2}
                        >
                          {analytics.usage_by_category.map((_, i) => (
                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} stroke="none" />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            background: '#fff',
                            border: '1px solid rgba(0,0,0,0.08)',
                            borderRadius: 8,
                            fontSize: 11
                          }}
                          formatter={(value, name, props) => {
                            const total = analytics.usage_by_category.reduce((s, d) => s + d.credits_used, 0);
                            const pct = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                            return [`${value} credits (${pct}%)`, props.payload.category || name];
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    {/* Color-coded legend */}
                    <div className="mt-2 space-y-1 max-h-20 overflow-y-auto">
                      {analytics.usage_by_category.map((entry, i) => (
                        <div key={i} className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span
                              className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                              style={{ background: CHART_COLORS[i % CHART_COLORS.length] }}
                            />
                            <span className="text-[10px] text-slate-600 truncate">{entry.category}</span>
                          </div>
                          <span className="text-[10px] font-semibold text-slate-700 flex-shrink-0">{entry.credits_used}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : <EmptyChart />}
              </ChartCard>
            </div>

            {/* 3️⃣ Credit Burn & Projection */}
            <div ref={creditBurnChartRef} className="h-full">
              <ChartCard
                title="Credit Burn & Projection"
                subtitle={analytics?.credit_burn?.days_remaining != null
                  ? `~${analytics.credit_burn.days_remaining} days until credits run out`
                  : 'Projection'}
                className="!p-4 h-full"
                isDark={isDark}
                onDownload={() => downloadChart(creditBurnChartRef, 'credit-burn-projection', 'credit-burn-projection')}
                downloading={downloadingChartKey === 'credit-burn-projection'}
              >
                {analytics?.credit_burn?.chart_data?.length > 0 ? (
                  <>
                    {/* Burn rate badge */}
                    <div className="flex items-center justify-end gap-2 mb-2">
                      <span className="text-[10px] text-slate-400">Burn rate</span>
                      <span className="text-xs font-bold text-[#F87217]">
                        {analytics.credit_burn.burn_rate ?? '?'} credits/day
                      </span>
                    </div>
                    <ResponsiveContainer width="100%" height={155}>
                      <ComposedChart
                        data={analytics.credit_burn.chart_data}
                        margin={{ top: 4, right: 20, left: 10, bottom: 25 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.06)' : '#f0f4f8'} vertical={false} />
                        <XAxis dataKey="date" stroke="#94a3b8" fontSize={9} tickMargin={4} label={{ value: 'Date', position: 'insideBottom', offset: -15, fontSize: 9, fill: '#94a3b8' }} />
                        <YAxis stroke="#94a3b8" fontSize={9} width={50} label={{ value: 'Credits', angle: -90, position: 'insideLeft', offset: 10, fontSize: 9, fill: '#94a3b8' }} />
                        <Tooltip
                          contentStyle={{
                            background: '#fff',
                            border: '1px solid rgba(0,0,0,0.08)',
                            borderRadius: 8,
                            fontSize: 11
                          }}
                          formatter={(v, name) => [
                            `${v} credits`,
                            name === 'cumulative' ? 'Actual' : 'Projected'
                          ]}
                        />
                        {/* Actual cumulative credits used */}
                        <Line
                          type="monotone"
                          dataKey="cumulative"
                          stroke="#F87217"
                          strokeWidth={2}
                          dot={{ fill: '#F87217', r: 3 }}
                          activeDot={{ r: 5 }}
                          name="cumulative"
                          connectNulls
                        />
                        {/* Projected line — dashed gray */}
                        <Line
                          type="monotone"
                          dataKey="projected"
                          stroke="#94a3b8"
                          strokeWidth={1.5}
                          strokeDasharray="5 4"
                          dot={false}
                          name="projected"
                          connectNulls
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                    {/* Legend */}
                    <div className="flex items-center gap-4 mt-2 justify-center">
                      <span className="flex items-center gap-1.5 text-[10px] text-slate-500">
                        <span className="w-4 h-0.5 bg-[#F87217] inline-block rounded" />
                        Actual
                      </span>
                      <span className="flex items-center gap-1.5 text-[10px] text-slate-500">
                        <span className="w-4 h-0.5 border-t border-dashed border-slate-400 inline-block" />
                        Projected
                      </span>
                    </div>
                  </>
                ) : <EmptyChart />}
              </ChartCard>
            </div>

            {/* 4️⃣ Queries by Day */}
            <div ref={promptsPerDayChartRef} className="h-full">
              <ChartCard
                title="Prompts / Day"
                subtitle="Weekly pattern"
                className="!p-4 h-full"
                isDark={isDark}
                onDownload={() => downloadChart(promptsPerDayChartRef, 'prompts-per-day', 'prompts-per-day')}
                downloading={downloadingChartKey === 'prompts-per-day'}
              >
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={analytics?.queries_by_dow} margin={{ top: 10, right: 20, left: 10, bottom: 25 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.06)' : '#f0f4f8'} />
                    <XAxis dataKey="day" stroke="#94a3b8" fontSize={10} tickMargin={5} label={{ value: 'Day of Week', position: 'insideBottom', offset: -15, fontSize: 9, fill: '#94a3b8' }} />
                    <YAxis stroke="#94a3b8" fontSize={10} width={50} label={{ value: 'Queries', angle: -90, position: 'insideLeft', offset: 10, fontSize: 9, fill: '#94a3b8' }} />
                    <Tooltip />
                    <Bar dataKey="query_count" fill="#F87217" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>

          </div>
        </div>

        {/* ── Favorite Agents ─────────────────────────────── */}
        {favorites.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: "'Outfit',sans-serif", color: isDark ? '#f0f6fc' : '#0d1a3a' }}>
                <Heart className="w-5 h-5 text-red-500 fill-current" />
                Your Favorites
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {favorites.map(agent => (
                <AgentMiniCard
                  key={agent.id} agent={agent}
                  onClick={() => navigate(`/agent/${agent.id}`)}
                  testId={`favorite-agent-${agent.id}`}
                  isDark={isDark}
                />
              ))}
            </div>
          </div>
        )}

        {/* ── My Agents ───────────────────────────────────── */}
        {myAgents.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: "'Outfit',sans-serif", color: isDark ? '#f0f6fc' : '#0d1a3a' }}>
                <Bot className="w-5 h-5 text-[#F87217]" />
                My Agents
              </h2>
              <button
                onClick={() => {
                  const _hasAccess = user?.plan === 'enterprise_pro' || (user?.feature_access || []).includes('agent_builder');
                  navigate(_hasAccess ? '/builder' : '/pricing');
                }}
                className="text-sm font-semibold transition-colors flex items-center gap-1"
                style={{ color: '#F87217' }}
              >
                Create New <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {myAgents.map(agent => (
                <div
                  key={agent.id}
                  className="relative"
                  onClick={() => navigate(`/agent/${agent.id}`)}
                >
                  <AgentMiniCard agent={agent} onClick={() => navigate(`/agent/${agent.id}`)} isDark={isDark} />
                  <span
                    className="absolute top-4 right-4 text-[10px] font-bold px-2 py-0.5 rounded-full"
                    style={{
                      background: agent.is_public
                        ? (isDark ? 'rgba(16,185,129,0.2)' : 'rgba(16,185,129,0.1)')
                        : (isDark ? 'rgba(148,163,184,0.2)' : 'rgba(100,116,139,0.1)'),
                      color: agent.is_public ? '#10b981' : (isDark ? '#cbd5e1' : '#64748b'),
                    }}
                  >
                    {agent.is_public ? 'Public' : 'Private'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Popular Agents ──────────────────────────────── */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-bold" style={{ fontFamily: "'Outfit',sans-serif", color: isDark ? '#f0f6fc' : '#0d1a3a' }}>Popular Agents</h2>
            <button
              onClick={() => navigate('/marketplace')}
              className="text-sm font-semibold transition-colors flex items-center gap-1"
              style={{ color: '#F87217' }}
              data-testid="view-all-agents"
            >
              View All <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {agents.slice(0, 4).map(agent => (
              <AgentMiniCard
                key={agent.id} agent={agent}
                onClick={() => navigate(`/agent/${agent.id}`)}
                testId={`recent-agent-${agent.id}`}
                isDark={isDark}
              />
            ))}
          </div>
        </div>


      </div>
    </div>
  );
};

export default Dashboard;